import { BehavioralMentalStatus } from "@/components/Provider/PatientView/MemberProfile/MemberProfileNotesMentalStatus";
import CardSecondary from "@/ui/Card/CardSecondary";
import dayjs from "@/lib/helpers/dayjs";
import {
  ExpandingSelect,
  IndividualSymptomTrend,
} from "@/components/Common/GuidedVideoCall/Steps/ProviderWorkflow/DisplayWorkflowStepWithVideoClient";
import { getAutomationDataAttr } from "@/utils/automationUtils";

import { BehavioralSymptomTrend } from "./components/MemberProfileNotesSymptomTrend";
import { DischargeContent } from "./(discharge)/DischargeContent";
import { GenericMultiselect } from "./components/MemberProfileNotesGenericMultiselect";
import { GenericSectionNotes } from "./components/MemberProfileNotesGenericNotes";
import MemberProfileNotesDuration from "./components/MemberProfileNotesDuration";
import { NotesDiagnosis } from "./components/MemberProfileNotesDiagnosis";
import { PrescribeDiagnosisProgress } from "./components/MemberProfileNotesPrescribeDiagnosisProgress";
import { PrescribeLabFindings } from "./components/MemberProfileNotesLabFindings";
import { PrescriberMedicationsV2 } from "./components/MemberProfileNotesMedications";

export function PrescribeNewNote({
  editable,
  editHandler,
  errors,
  isCallManuallyLinked,
  note,
  profile,
  views,
}) {
  let { createEditView, profileView } = views;

  let medications = (
    <PrescriberMedicationsV2
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profileView={profileView}
    />
  );

  let diagnosisProgress = (
    <PrescribeDiagnosisProgress
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
    />
  );

  let symptomTrendNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="symptom_trend_notes"
      sectionLabel="Symptom Trends/Review"
    />
  );

  let medicationImpactNote = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="medication_impact_notes"
      sectionLabel="Medication Impact"
    />
  );

  let medicalInformationNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      renderExtra={() => {
        let prescriptionMonitoring = note.content.prescription_monitoring;

        return (
          <div className="ml-2">
            <input
              {...getAutomationDataAttr("medical-information-note-i-stop")}
              checked={!!prescriptionMonitoring.clicked}
              disabled={!editable}
              onChange={
                editable
                  ? (e) =>
                      editHandler("prescription_monitoring", {
                        ...prescriptionMonitoring,
                        clicked: e.target.checked,
                        last_viewed: e.target.checked
                          ? dayjs.utc().toISOString()
                          : prescriptionMonitoring.last_viewed,
                      })
                  : undefined
              }
              type="checkbox"
            />
            <span className="ml-2">{prescriptionMonitoring.type}</span>
          </div>
        );
      }}
      sectionKey="medical_information_notes"
      sectionLabel="Medical Information"
    />
  );

  let mentalStatus = (
    <BehavioralMentalStatus
      createEditView={createEditView}
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      showTitle={true}
    />
  );

  let symptomTrends;

  if (note.content.last_behavioral_note) {
    symptomTrends = (
      <BehavioralSymptomTrend
        editable={false}
        editHandler={editHandler}
        note={note.content.last_behavioral_note}
        patientAnswersEditable={false}
        profileView={profileView}
        showProvider={true}
        smallerDimensions={true}
      />
    );
  } else {
    symptomTrends = (
      <CardSecondary className="mx-0 px-3 py-3 my-3 w-100">
        Patient has not had previous behavioral guided session for trends.
      </CardSecondary>
    );
  }

  let adherenceInterventions = (
    <GenericMultiselect
      editable={editable}
      editHandler={editHandler}
      isCheckboxes={true}
      labelClass=""
      note={note}
      profileView={profileView}
      sectionKey="adherence_interventions"
      sectionLabel="Adherence Interventions"
    />
  );

  let medicalProblems = (
    <ExpandingSelect
      className="m-3 grey-container"
      disabled
      label="Medical Problems"
      labelClass="tw:font-bold clr-sec"
      options={note.content.medical_problems}
    />
  );

  let overallTrend = <IndividualSymptomTrend answer={note.content.symptom_trends.overall} />;

  let individualTrends = ["frequency", "effectiveness", "problems", "stop"]
    .filter((key) => note.content.medication_trends[key])
    .map((key) => (
      <IndividualSymptomTrend answer={note.content.medication_trends[key]} key={key} />
    ));

  let medicationProblems = (
    <ExpandingSelect
      className="m-3 grey-container"
      disabled
      label="What makes it hard to take your medication?"
      labelClass="tw:font-bold clr-sec fs-13"
      options={note.content.medication_difficulties}
    />
  );

  let sideEffects = (
    <GenericMultiselect
      editable={editable}
      editHandler={editHandler}
      isCheckboxes={true}
      labelClass=""
      note={note}
      profileView={profileView}
      sectionKey="side_effects"
      sectionLabel="Side Effect(s)"
      // noneSelectedMessage='No side effects observed or reported.'
    />
  );

  let labFindings = (
    <PrescribeLabFindings
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
    />
  );

  let diagnosis = (
    <NotesDiagnosis
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profile={profile}
      profileView={profileView}
    />
  );

  let duration = !isCallManuallyLinked ? (
    <MemberProfileNotesDuration
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profileView={profileView}
    />
  ) : (
    <></>
  );

  let discharges = Object.hasOwn(note.content, "discharges") ? (
    <DischargeContent
      classes={{ card: editable ? "CardSeconday py-2" : null, colReadonly: "col-5" }}
      dischargeValues={note.content.discharges}
      editable={editable}
      setDischargeValues={(val, _key, nested) => editHandler("discharges", val, nested)}
      version="2"
    />
  ) : null;

  let bottomArea;

  if (createEditView) {
    bottomArea = (
      <div className="p-3">
        {diagnosis}
        {duration}
        {discharges}
      </div>
    );
  } else if (profileView) {
    bottomArea = (
      <>
        {diagnosis}
        {duration}
        {discharges}
      </>
    );
  }

  let otherCss = profileView ? "px-0 mx-0 w-100" : "m-auto";
  let desktopContent = (
    <div className="d-none d-md-block d-lg-block">
      <div>{medications}</div>
      <div>{diagnosisProgress}</div>
      <div className={`row ${otherCss}`}>
        <div className="col-8 px-0">
          <div className="row px-0">
            <div className="col-6">
              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {symptomTrendNotes}
              </div>
              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {medicationImpactNote}
              </div>
              <div className={profileView ? "p-3 border-right-note" : ""}>
                {medicalInformationNotes}
              </div>
              <div className={profileView ? "p-3 border-right-note" : ""}>{mentalStatus}</div>
            </div>
            <div className="col-6">
              <div className={profileView ? "p-3 border-right-note" : ""}>{symptomTrends}</div>
              <div
                className={
                  profileView ? "p-3 border-top-note border-right-note border-bottom-note" : ""
                }
              >
                {adherenceInterventions}
              </div>
            </div>
          </div>
          <div className="row">
            <div className={profileView ? "p-3" : ""}>{bottomArea}</div>
          </div>
        </div>

        <div className="col-4 px-0">
          <div className="row px-0">
            <div className="col-12">
              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {medicalProblems}
              </div>
              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {overallTrend}
              </div>
              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : ""
                }
              >
                {individualTrends}
              </div>
              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : ""
                }
              >
                {medicationProblems}
              </div>
              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : "px-3"
                }
              >
                {sideEffects}
              </div>
              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : ""
                }
              >
                {labFindings}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  let mobileContent = (
    <div className="d-block d-md-none d-lg-none">
      {medications}
      {diagnosisProgress}
      {symptomTrendNotes}
      {medicationImpactNote}
      {medicalInformationNotes}
      {mentalStatus}
      {symptomTrends}
      {adherenceInterventions}
      {medicalProblems}
      {overallTrend}
      {individualTrends}
      {medicalProblems}
      {sideEffects}
      {labFindings}
      {duration}
      {discharges}
      {bottomArea}
    </div>
  );

  return (
    <>
      {desktopContent}
      {mobileContent}
    </>
  );
}
